// src/components/ReceiptModal/ReceiptModal.js
import React, { useRef } from "react";
import "./ReceiptModal.css";

const ReceiptModal = ({ sale, onClose }) => {
  const receiptRef = useRef();

  const resolveUnitPrice = (item) => {
    const val = item.unit_price ?? item.price ?? 0;
    return Number(val) || 0;
  };

  const resolveSubtotal = (item) => {
    if (item.subtotal != null) return Number(item.subtotal) || 0;
    return resolveUnitPrice(item) * (Number(item.quantity) || 0);
  };

  const isVoided = sale.status === "voided";
  const statusLabel = sale.status ? sale.status.toUpperCase() : "COMPLETED";
  const pharmacyName = sale.pharmacy_name || "Pharmacy";
  const hasWholesaleItems = (sale.items || []).some((i) => i.price_type === "wholesale");

  const addressParts = [
    sale.pharmacy_address,
    sale.pharmacy_city,
    sale.pharmacy_state,
  ].filter(Boolean);
  const addressLine = addressParts.join(", ");

  const displayPhone = sale.branch_phone || sale.pharmacy_phone || null;

  // ── Discount helpers ──
  const hasDiscount = sale.discount_amount && parseFloat(sale.discount_amount) > 0;
  const subtotalBeforeDiscount = sale.subtotal != null
    ? Number(sale.subtotal)
    : Number(sale.total_amount);

  const receiptStyles = `
    body {
      font-family: 'Courier New', monospace;
      padding: 20px;
      max-width: 400px;
      margin: 0 auto;
    }
    .receipt-header {
      text-align: center;
      border-bottom: 2px dashed #000;
      padding-bottom: 10px;
      margin-bottom: 15px;
    }
    .receipt-header h2 {
      margin: 5px 0;
      font-size: 1.5rem;
    }
    .receipt-header p {
      margin: 3px 0;
      font-size: 0.85rem;
      color: #444;
    }
    .receipt-info {
      margin-bottom: 15px;
      font-size: 0.9rem;
    }
    .receipt-info p {
      margin: 3px 0;
    }
    .voided-stamp {
      text-align: center;
      font-size: 2rem;
      font-weight: bold;
      color: #dc3545;
      border: 3px solid #dc3545;
      padding: 10px;
      margin: 15px 0;
      transform: rotate(-15deg);
    }
    .wholesale-banner {
      text-align: center;
      background: #e3f2fd;
      border: 1px solid #1565c0;
      color: #1565c0;
      font-weight: bold;
      font-size: 0.85rem;
      padding: 4px 8px;
      margin: 8px 0;
      border-radius: 4px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 15px;
    }
    th, td {
      text-align: left;
      padding: 5px;
      border-bottom: 1px solid #ddd;
    }
    th { font-weight: bold; }
    .ws-tag {
      font-size: 0.7rem;
      color: #1565c0;
      background: #e3f2fd;
      padding: 1px 4px;
      border-radius: 3px;
      font-weight: bold;
      margin-left: 4px;
    }
    .receipt-summary {
      border-top: 1px dashed #000;
      padding-top: 8px;
      margin-bottom: 8px;
      font-size: 0.9rem;
    }
    .receipt-summary .row {
      display: flex;
      justify-content: space-between;
      margin: 2px 0;
    }
    .receipt-summary .discount-row {
      color: #c62828;
    }
    .receipt-total {
      border-top: 2px solid #000;
      padding-top: 10px;
      font-size: 1.1rem;
      font-weight: bold;
      text-align: right;
    }
    .receipt-footer {
      text-align: center;
      margin-top: 20px;
      border-top: 2px dashed #000;
      padding-top: 10px;
      font-size: 0.85rem;
    }
  `;

  const handlePrint = async () => {
    const htmlContent = receiptRef.current.innerHTML;

    if (window.electronAPI?.printReceipt) {
      try {
        await window.electronAPI.printReceipt(htmlContent);
      } catch (err) {
        console.error("Electron print failed:", err);
      }
    } else {
      const printContent = receiptRef.current;
      const windowPrint = window.open("", "", "width=800,height=600");
      windowPrint.document.write("<html><head><title>Receipt</title>");
      windowPrint.document.write(`<style>${receiptStyles}</style>`);
      windowPrint.document.write("</head><body>");
      windowPrint.document.write(printContent.innerHTML);
      windowPrint.document.write("</body></html>");
      windowPrint.document.close();
      windowPrint.focus();
      windowPrint.print();
      windowPrint.close();
    }
  };

  return (
    <div className="receipt-modal-overlay" onClick={onClose}>
      <div className="receipt-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="receipt-close-btn" onClick={onClose}>✖</button>

        <div ref={receiptRef} className="receipt-content">
          {/* Header */}
          <div className="receipt-header">
            <h2>{pharmacyName}</h2>

            {sale.branch_name && (
              <p style={{ fontSize: "0.85rem", color: "#555", margin: "3px 0", fontWeight: "600" }}>
                {sale.branch_name}
              </p>
            )}

            {addressLine && (
              <p style={{ fontSize: "0.85rem", color: "#555", margin: "3px 0" }}>
                {addressLine}
              </p>
            )}

            {displayPhone && (
              <p style={{ fontSize: "0.85rem", color: "#555", margin: "3px 0" }}>
                Tel: {displayPhone}
                {sale.branch_phone && sale.pharmacy_phone && sale.branch_phone !== sale.pharmacy_phone && (
                  <span style={{ color: "#999", fontSize: "0.78rem" }}> (branch)</span>
                )}
              </p>
            )}

            <p style={{ marginTop: "6px" }}>Receipt #{sale.receipt_number}</p>
            <p>{new Date(sale.created_at).toLocaleString()}</p>
          </div>

          {/* Voided Stamp */}
          {isVoided && <div className="voided-stamp">VOIDED</div>}

          {/* Wholesale banner — shown when any item was sold at wholesale price */}
          {hasWholesaleItems && (
            <div className="wholesale-banner">
              📦 WHOLESALE TRANSACTION
            </div>
          )}

          {/* Receipt Info */}
          <div className="receipt-info">
            <p><strong>Status:</strong> {statusLabel}</p>
            {isVoided && sale.voided_at && (
              <p style={{ color: "#dc3545" }}>
                <strong>Voided At:</strong> {new Date(sale.voided_at).toLocaleString()}
              </p>
            )}
            {isVoided && sale.voided_by_name && (
              <p style={{ color: "#dc3545" }}>
                <strong>Voided By:</strong> {sale.voided_by_name}
              </p>
            )}
          </div>

          {/* Items Table */}
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {(sale.items || []).length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: "center", color: "#999" }}>No items</td>
                </tr>
              ) : (
                (sale.items || []).map((item, index) => (
                  <tr key={index}>
                    <td>
                      {item.name}
                      {/* ── W/S tag on wholesale items ── */}
                      {item.price_type === "wholesale" && (
                        <span className="ws-tag">W/S</span>
                      )}
                    </td>
                    <td>{item.quantity}</td>
                    <td>₦{resolveUnitPrice(item).toFixed(2)}</td>
                    <td>₦{resolveSubtotal(item).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* ── Summary with discount breakdown ── */}
          {hasDiscount ? (
            <div className="receipt-summary">
              <div className="row">
                <span>Subtotal</span>
                <span>₦{subtotalBeforeDiscount.toFixed(2)}</span>
              </div>
              <div className="row discount-row">
                <span>
                  Discount
                  {sale.discount_type === "percentage"
                    ? ` (${sale.discount_value}%)`
                    : ` (₦${sale.discount_value} flat)`}
                </span>
                <span>- ₦{parseFloat(sale.discount_amount).toFixed(2)}</span>
              </div>
            </div>
          ) : null}

          {/* Total */}
          <div className="receipt-total">
            <p>{hasDiscount ? "TOTAL CHARGED" : "TOTAL"}: ₦{(Number(sale.total_amount) || 0).toFixed(2)}</p>
          </div>

          {/* Footer */}
          <div className="receipt-footer">
            <p>Thank you for your patronage!</p>
            <p>Please keep this receipt for your records</p>
          </div>
        </div>

        <div className="receipt-actions">
          <button className="receipt-print-btn" onClick={handlePrint}>🖨 Print Receipt</button>
          <button className="receipt-cancel-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;